import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import React, { useCallback, useEffect, useState } from 'react';
import { UpdateSavedSearch } from 'services/SavedSearchesService';

interface Props {
  open: boolean;
  ssid: number;
  name: string;
  frequency: number;
  disableFrequencyField?: boolean;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  successHandler?: (
    message: string,
    ssid: number,
    newName: string,
    newFreq: number,
    newFreqName: string
  ) => void;
  errorHandler?: (message: string) => void;
}

const frequencyNameMap: Record<number, string> = {
  0: 'Manual',
  1: 'Hourly',
  2: 'Daily',
  3: 'Weekly',
  4: 'Monthly',
};

export default function EditSaveSearchDialog({
  open,
  ssid,
  name: nameProp,
  frequency: frequencyProp,
  disableFrequencyField = false,
  closeHandler,
  loadingHandler,
  successHandler,
  errorHandler,
}: Props) {
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState(0);

  useEffect(() => {
    if (open) {
      setName(nameProp);
      setFrequency(frequencyProp);
    }
  }, [open, nameProp, frequencyProp]);

  const saveCallback = useCallback(async () => {
    loadingHandler && loadingHandler(true);
    const res = await UpdateSavedSearch(ssid, name, frequency, errorHandler);
    if (res) {
      successHandler &&
        successHandler('Saved changes', ssid, name, frequency, frequencyNameMap[frequency]);
      closeHandler();
    }
    loadingHandler && loadingHandler(false);
  }, [name, frequency]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>Edit Save Search</DialogTitle>
      <DialogContent>
        <Stack spacing={2} paddingTop="10px">
          <RWTextFieldComponent
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            select
            label="Auto Run Frequency"
            value={frequency.toString()}
            onChange={({ target }) => setFrequency(+target.value)}
            disabled={disableFrequencyField}
          >
            <MenuItem value="0">Manual</MenuItem>
            <MenuItem value="1">Hourly</MenuItem>
            <MenuItem value="2">Daily</MenuItem>
            <MenuItem value="3">Weekly</MenuItem>
            <MenuItem value="4">Monthly</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={name === '' || (name === nameProp && frequency === frequencyProp)}
          color="success"
          onClick={saveCallback}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
